import { CheckCircle, RadioButtonUnchecked, HourglassBottom } from '@mui/icons-material';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApplicationInterface } from 'types';

interface Props {
    application?: ApplicationInterface;
    isAllDocumentsCompletedFlag?: boolean;
}

type StepStatus = 'completed' | 'in-progress' | 'pending';

interface StepObject {
    name: string;
    status: StepStatus;
}

// - Step 1. Assigned to representative
// - Step 2. Service agreement signed
// - Step 3. Gathering Documents / Reviewing Documents
// - Step 4. File Ready for Submission / File Submitted
// - Step 5. Service Completed

const ApplicationProgressStepper = ({ application, isAllDocumentsCompletedFlag }: Props) => {
    const { t } = useTranslation();

    const steps = useMemo((): StepObject[] => {
        if (!application) return [];

        const stepsArray: StepObject[] = [];

        // Step 1: Assigned to representative
        stepsArray.push(
            application.consultant_name
                ? { name: 'Assigned to Representative', status: 'completed' }
                : { name: 'Waiting to be assigned', status: 'in-progress' }
        );

        // Step 2: Service agreement signed
        stepsArray.push(
            application.date_agreement_signed
                ? { name: 'Service Agreement Signed', status: 'completed' }
                : { name: 'Service Agreement Pending', status: 'in-progress' }
        );

        // Step 3: Document gathering status
        stepsArray.push(
            isAllDocumentsCompletedFlag
                ? { name: 'Reviewing Documents', status: 'completed' }
                : { name: 'Gathering Documents', status: 'in-progress' }
        );

        // Step 4: File ready for submission
        if (application.client_status === 'SUBMITTED') {
            stepsArray.push({ name: 'File Submitted', status: 'completed' });
        } else if (application.client_status === 'READY_FOR_SUBMISSION') {
            stepsArray.push({ name: 'File Ready for Submission', status: 'completed' });
        } else {
            stepsArray.push({ name: 'File Ready for Submission', status: 'pending' });
        }

        // Step 5: Service completion
        stepsArray.push(
            application.status === 'SUCCESS'
                ? { name: 'Service Completed', status: 'completed' }
                : { name: 'Service Completed', status: 'pending' }
        );

        return stepsArray;
    }, [application, isAllDocumentsCompletedFlag]);

    const completedCount = steps.filter((s) => s.status === 'completed').length;
    const totalSteps = steps.length;
    const percent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
    const isAllDone = percent === 100;

    if (!application || steps.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
            }}
        >
            {/* ── Header: title, percentage, summary, progress bar ── */}
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.75,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.938rem' }}>
                        {t('visaApplicationProgress')}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            fontSize: '1.25rem',
                            lineHeight: 1,
                            background: isAllDone
                                ? '#10b981'
                                : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        {percent}%
                    </Typography>
                </Box>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}
                >
                    {completedCount} of {totalSteps} steps completed
                </Typography>

                <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: isAllDone
                                ? '#10b981'
                                : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            transition: 'transform 0.6s ease',
                        },
                    }}
                />
            </Box>

            {/* ── Steps list ── */}
            <Box sx={{ px: 1.5, pb: 1.5 }}>
                {steps.map((step, index) => {
                    const isLast = index === steps.length - 1;

                    return (
                        <Box
                            key={`${step.name}-${index}`}
                            sx={{ display: 'flex', alignItems: 'stretch' }}
                        >
                            {/* Left rail: icon + connector */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 32,
                                    flexShrink: 0,
                                    mr: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 28,
                                        height: 28,
                                        flexShrink: 0,
                                    }}
                                >
                                    <StepIcon status={step.status} />
                                </Box>

                                {!isLast && (
                                    <Box
                                        sx={{
                                            width: 2,
                                            flexGrow: 1,
                                            minHeight: 12,
                                            bgcolor:
                                                step.status === 'completed'
                                                    ? 'rgba(16, 185, 129, 0.3)'
                                                    : 'rgba(148, 163, 184, 0.2)',
                                            borderRadius: 1,
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Right: label + status badge */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    pb: isLast ? 0.5 : 1.5,
                                    minHeight: 28,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight:
                                            step.status === 'in-progress'
                                                ? 600
                                                : step.status === 'completed'
                                                  ? 500
                                                  : 400,
                                        color:
                                            step.status === 'pending'
                                                ? 'text.secondary'
                                                : 'text.primary',
                                        fontSize: '0.813rem',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {step.name}
                                </Typography>

                                <StatusBadge status={step.status} />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

/* ─── Sub-components ─── */

const statusColors: Record<StepStatus, { icon: string; badge: string; badgeBg: string }> = {
    completed: { icon: '#10b981', badge: '#059669', badgeBg: 'rgba(16, 185, 129, 0.10)' },
    'in-progress': { icon: '#6366f1', badge: '#6366f1', badgeBg: 'rgba(99, 102, 241, 0.10)' },
    pending: { icon: '#94a3b8', badge: '#94a3b8', badgeBg: 'rgba(148, 163, 184, 0.08)' },
};

const StepIcon = ({ status }: { status: StepStatus }) => {
    const colors = statusColors[status];

    switch (status) {
        case 'completed':
            return <CheckCircle sx={{ fontSize: 22, color: colors.icon }} />;
        case 'in-progress':
            return (
                <HourglassBottom
                    sx={{
                        fontSize: 22,
                        color: colors.icon,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                        },
                    }}
                />
            );
        case 'pending':
        default:
            return <RadioButtonUnchecked sx={{ fontSize: 22, color: colors.icon }} />;
    }
};

const statusLabels: Record<StepStatus, string> = {
    completed: 'Completed',
    'in-progress': 'In Progress',
    pending: 'Pending',
};

const StatusBadge = ({ status }: { status: StepStatus }) => {
    const colors = statusColors[status];

    return (
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: colors.badgeBg, flexShrink: 0 }}>
            <Typography
                variant="caption"
                sx={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: colors.badge,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                }}
            >
                {statusLabels[status]}
            </Typography>
        </Box>
    );
};

export default ApplicationProgressStepper;
